<template>
  <div class="challenges-section">
    <!-- Login Required Message -->
    <div v-if="!isAuthenticated" class="row">
      <div class="col-12">
        <div class="card border-primary">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="bi bi-person-lock display-1 text-primary"></i>
            </div>
            <h4 class="card-title mb-3">Login Required</h4>
            <p class="card-text text-muted mb-4">
              Please log in to join fitness challenges and compete with the community.
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <router-link to="/login" class="btn btn-primary px-4">
                <i class="bi bi-box-arrow-in-right me-2"></i>Login
              </router-link>
              <router-link to="/register" class="btn btn-outline-primary px-4">
                <i class="bi bi-person-plus me-2"></i>Sign Up
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authenticated Content -->
    <div v-else class="row g-4">
      <!-- Active Challenges -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-trophy me-2"></i>Active Challenges</h5>
            <button class="btn btn-primary btn-sm" @click="showJoinModal = true">
              <i class="bi bi-plus me-1"></i>Join Challenge
            </button>
          </div>
          <div class="card-body">
            <div v-if="activeChallenges.length === 0" class="text-center py-4">
              <i class="bi bi-trophy text-muted" style="font-size: 3rem"></i>
              <p class="text-muted mt-2">No active challenges. Join one to start your journey!</p>
            </div>

            <div v-else>
              <div
                v-for="challenge in activeChallenges"
                :key="challenge.id"
                class="challenge-card mb-3"
              >
                <div class="challenge-header d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h6 class="challenge-title">{{ challenge.name }}</h6>
                    <p class="challenge-description text-muted mb-0">{{ challenge.description }}</p>
                  </div>
                  <span class="badge bg-primary">{{ challenge.daysLeft }} days left</span>
                </div>

                <div class="challenge-progress mb-2">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <small class="text-muted">Progress</small>
                    <small class="text-muted"
                      >{{ challenge.progress }}/{{ challenge.target }} {{ challenge.unit }}</small
                    >
                  </div>
                  <div class="progress">
                    <div
                      class="progress-bar"
                      :class="'bg-' + challenge.color"
                      :style="{ width: (challenge.progress / challenge.target) * 100 + '%' }"
                    ></div>
                  </div>
                </div>

                <div class="challenge-actions d-flex gap-2">
                  <button class="btn btn-sm btn-outline-success" @click="updateProgress(challenge)">
                    <i class="bi bi-plus"></i> Update Progress
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="leaveChallenge(challenge.id)"
                  >
                    <i class="bi bi-x"></i> Leave
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Available Challenges & Statistics -->
      <div class="col-lg-4">
        <div class="card mb-4">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-star me-2"></i>Available Challenges</h6>
          </div>
          <div class="card-body">
            <div
              v-for="challenge in availableChallenges"
              :key="challenge.id"
              class="available-challenge d-flex justify-content-between align-items-center p-2 mb-2 border rounded"
            >
              <div>
                <strong class="d-block">{{ challenge.name }}</strong>
                <small class="text-muted"
                  >{{ challenge.duration }} days • {{ challenge.difficulty }}</small
                >
              </div>
              <button class="btn btn-sm btn-primary" @click="joinChallenge(challenge)">Join</button>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-graph-up me-2"></i>Your Statistics</h6>
          </div>
          <div class="card-body">
            <div class="stat-item d-flex justify-content-between mb-2">
              <span>Completed Challenges</span>
              <strong class="text-success">{{ completedChallenges }}</strong>
            </div>
            <div class="stat-item d-flex justify-content-between mb-2">
              <span>Success Rate</span>
              <strong class="text-info">{{ successRate }}%</strong>
            </div>
            <div class="stat-item d-flex justify-content-between mb-2">
              <span>Current Streak</span>
              <strong class="text-warning">{{ currentStreak }} days</strong>
            </div>
            <div class="stat-item d-flex justify-content-between">
              <span>Total Points</span>
              <strong class="text-primary">{{ totalPoints }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Join Challenge Modal -->
    <div
      v-if="showJoinModal"
      class="modal d-block"
      style="background: rgba(0, 0, 0, 0.5)"
      @click.self="showJoinModal = false"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Join a Challenge</h5>
            <button type="button" class="btn-close" @click="showJoinModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div v-for="challenge in availableChallenges" :key="challenge.id" class="col-md-6">
                <div class="card challenge-option" @click="selectChallenge(challenge)">
                  <div class="card-body">
                    <h6 class="card-title">{{ challenge.name }}</h6>
                    <p class="card-text text-muted small">{{ challenge.description }}</p>
                    <div class="challenge-details">
                      <small class="badge bg-light text-dark me-1"
                        >{{ challenge.duration }} days</small
                      >
                      <small class="badge bg-light text-dark me-1">{{
                        challenge.difficulty
                      }}</small>
                      <small class="badge bg-light text-dark">{{ challenge.points }} points</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Update Progress Modal -->
    <div
      v-if="showUpdateModal"
      class="modal d-block"
      style="background: rgba(0, 0, 0, 0.5)"
      @click.self="showUpdateModal = false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Update Progress</h5>
            <button type="button" class="btn-close" @click="showUpdateModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedChallenge">
              <h6>{{ selectedChallenge.name }}</h6>
              <p class="text-muted">{{ selectedChallenge.description }}</p>

              <div class="mb-3">
                <label class="form-label">Add Progress ({{ selectedChallenge.unit }})</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="progressUpdate"
                  :placeholder="'Enter amount in ' + selectedChallenge.unit"
                  min="0"
                />
              </div>

              <div class="current-progress p-3 bg-light rounded">
                <div class="d-flex justify-content-between mb-1">
                  <span>Current Progress:</span>
                  <span
                    >{{ selectedChallenge.progress }}/{{ selectedChallenge.target }}
                    {{ selectedChallenge.unit }}</span
                  >
                </div>
                <div class="progress">
                  <div
                    class="progress-bar"
                    :class="'bg-' + selectedChallenge.color"
                    :style="{
                      width: (selectedChallenge.progress / selectedChallenge.target) * 100 + '%',
                    }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showUpdateModal = false">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="saveProgress">
              Update Progress
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'

export default {
  name: 'ChallengesSection',
  setup() {
    const { user, isAuthenticated, initAuth } = useFirebaseAuth()
    return { user, isAuthenticated, initAuth }
  },
  data() {
    return {
      activeChallenges: [],
      showJoinModal: false,
      showUpdateModal: false,
      selectedChallenge: null,
      progressUpdate: '',
      completedChallenges: 5,
      successRate: 80,
      currentStreak: 7,
      totalPoints: 2450,
      availableChallenges: [
        {
          id: 1,
          name: '30-Day Step Challenge',
          description: 'Walk 10,000 steps daily for 30 days',
          duration: 30,
          difficulty: 'Beginner',
          target: 300000,
          unit: 'steps',
          points: 500,
          color: 'primary',
        },
        {
          id: 2,
          name: 'Water Warrior',
          description: 'Drink 8 glasses of water daily for 21 days',
          duration: 21,
          difficulty: 'Easy',
          target: 168,
          unit: 'glasses',
          points: 300,
          color: 'info',
        },
        {
          id: 3,
          name: 'Morning Yoga',
          description: '15 minutes of yoga every morning for 14 days',
          duration: 14,
          difficulty: 'Beginner',
          target: 210,
          unit: 'minutes',
          points: 400,
          color: 'success',
        },
        {
          id: 4,
          name: 'Strength Builder',
          description: 'Complete 3 strength workouts per week for 4 weeks',
          duration: 28,
          difficulty: 'Intermediate',
          target: 12,
          unit: 'workouts',
          points: 600,
          color: 'warning',
        },
        {
          id: 5,
          name: 'Mindful Eating',
          description: 'Log all meals for 21 days',
          duration: 21,
          difficulty: 'Easy',
          target: 63,
          unit: 'meals',
          points: 350,
          color: 'danger',
        },
      ],
    }
  },

  mounted() {
    this.initAuth()
    if (this.isAuthenticated) {
      this.loadChallenges()
    }
  },

  methods: {
    loadChallenges() {
      const stored = localStorage.getItem('activeChallenges')
      if (stored) {
        this.activeChallenges = JSON.parse(stored)
      }
    },

    saveChallenges() {
      localStorage.setItem('activeChallenges', JSON.stringify(this.activeChallenges))
    },

    joinChallenge(challenge) {
      const newChallenge = {
        ...challenge,
        progress: 0,
        startDate: new Date().toISOString().split('T')[0],
        daysLeft: challenge.duration,
      }

      this.activeChallenges.push(newChallenge)
      this.saveChallenges()
      this.showJoinModal = false

      // Remove from available challenges temporarily
      this.availableChallenges = this.availableChallenges.filter((c) => c.id !== challenge.id)
    },

    selectChallenge(challenge) {
      this.joinChallenge(challenge)
    },

    leaveChallenge(challengeId) {
      if (confirm('Are you sure you want to leave this challenge?')) {
        const challenge = this.activeChallenges.find((c) => c.id === challengeId)
        this.activeChallenges = this.activeChallenges.filter((c) => c.id !== challengeId)
        this.saveChallenges()

        // Add back to available challenges
        if (challenge) {
          this.availableChallenges.push({
            id: challenge.id,
            name: challenge.name,
            description: challenge.description,
            duration: challenge.duration,
            difficulty: challenge.difficulty,
            target: challenge.target,
            unit: challenge.unit,
            points: challenge.points,
            color: challenge.color,
          })
        }
      }
    },

    updateProgress(challenge) {
      this.selectedChallenge = challenge
      this.progressUpdate = ''
      this.showUpdateModal = true
    },

    saveProgress() {
      if (this.selectedChallenge && this.progressUpdate) {
        const additional = parseFloat(this.progressUpdate)
        this.selectedChallenge.progress = Math.min(
          this.selectedChallenge.progress + additional,
          this.selectedChallenge.target
        )

        // Check if challenge is completed
        if (this.selectedChallenge.progress >= this.selectedChallenge.target) {
          this.completeChallenge(this.selectedChallenge)
        }

        this.saveChallenges()
        this.showUpdateModal = false
        this.selectedChallenge = null
        this.progressUpdate = ''
      }
    },

    completeChallenge(challenge) {
      // Show completion message
      setTimeout(() => {
        alert(
          `Congratulations! You completed the "${challenge.name}" challenge and earned ${challenge.points} points!`
        )

        // Update statistics
        this.completedChallenges++
        this.totalPoints += challenge.points

        // Remove from active challenges
        this.activeChallenges = this.activeChallenges.filter((c) => c.id !== challenge.id)
        this.saveChallenges()

        // Add back to available challenges for re-joining
        this.availableChallenges.push({
          id: challenge.id,
          name: challenge.name,
          description: challenge.description,
          duration: challenge.duration,
          difficulty: challenge.difficulty,
          target: challenge.target,
          unit: challenge.unit,
          points: challenge.points,
          color: challenge.color,
        })
      }, 500)
    },
  },
}
</script>

<style scoped>
.challenge-card {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.challenge-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.challenge-title {
  color: #495057;
  margin-bottom: 0.5rem;
}

.challenge-description {
  font-size: 0.9rem;
}

.challenge-progress .progress {
  height: 8px;
  border-radius: 4px;
}

.available-challenge {
  transition: all 0.3s ease;
  cursor: pointer;
}

.available-challenge:hover {
  background-color: #f8f9fa;
  transform: translateX(5px);
}

.challenge-option {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.challenge-option:hover {
  border-color: var(--bs-primary);
  transform: translateY(-2px);
}

.stat-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f3f4;
}

.stat-item:last-child {
  border-bottom: none;
}

.modal {
  z-index: 1050;
}

.current-progress {
  border-left: 4px solid var(--bs-primary);
}

@media (max-width: 768px) {
  .challenge-card {
    padding: 1rem;
  }

  .challenge-actions {
    flex-direction: column;
  }

  .challenge-actions .btn {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .available-challenge {
    flex-direction: column;
    align-items: flex-start;
  }

  .available-challenge .btn {
    margin-top: 0.5rem;
    align-self: flex-end;
  }
}
</style>
